import * as z from 'zod';

import config from '@/config';
import { LANGUAGES } from '@/languages';

export { LANGUAGES };

export const TABLET_BREAKPOINT = 767;
export const DESKTOP_BREAKPOINT = 1024;
export const SERVICE_WORKER_CHANNEL_NAME = 'service-worker-channel';

export const ISSUE_LANG_KEY = 'issue-lang';
export const USER_ROLE = 'user-role';
export const FAVORITE_KEY = 'favorites';

export const ACCESS_TOKEN_KEY = 'access_token';
export const ACCESS_TOKEN_EXPIRE_AT_KEY = 'token-expired-at';
export const BUILD_NUMBER = 'build';

export const APP_USER_ID = 'app-user-id';

export const ALERT_TIMEOUT_KEYS = {
	ID: 'alert-timeout-id',
	DATETIME: 'alert-timeout-datetime',
};

export const NAVBAR_ICON_URL =
	config.build.assetDirectory && config.build.assetDirectory !== '/'
		? `${config.build.assetDirectory}/assets/navbar-icon.png`
		: `/assets/navbar-icon.png?build=${config.build.buildNumber}`;

export const APP_ICON_URL =
	config.build.assetDirectory && config.build.assetDirectory !== '/'
		? `${config.build.assetDirectory}/assets/favicon-196.png?build=${config.build.buildNumber}`
		: `/assets/favicon-196.png?build=${config.build.buildNumber}`;

export const LangEnum = z.enum([...config.build.languages]).optional();

export const basepath = config?.build?.subdirectory
	? config.build.subdirectory
	: undefined;

export const MANIFEST_ICON_URL = config.build.assetDirectory
	? `${
			config.build.assetDirectory === '/'
				? ''
				: config.build.assetDirectory
		}/assets/manifest-icon-512.png?build=${config.build.buildNumber}`
	: `/assets/manifest-icon-512.png?build=${config.build.buildNumber}`;

// Deliberately narrower than `components/article/virtual/component.type.ts`'s
// `Component` -- constants.ts is a root-level file imported broadly and
// shouldn't depend on the article-renderer subsystem (topic 15's future
// standalone-package extraction target) for one recursive-filter helper.
// Only the two fields this function actually reads are named; everything
// else passes through the index signature untouched, same pattern
// `Component` itself uses for the same underlying schema gap (see
// `Article.components?: unknown` above).
interface ExcludableComponent {
	excludedchannels?: Array<string>;
	columns?: Array<Array<ExcludableComponent> | null>;
	[key: string]: unknown;
}

export function reduceExcludeComponents(
	acc: Array<ExcludableComponent>,
	component: ExcludableComponent,
): Array<ExcludableComponent> {
	component = clone(component);
	const { excludedchannels } = component;
	if (component.columns) {
		component.columns = component.columns.map((column) => {
			return column ? column.reduce(reduceExcludeComponents, []) : column;
		});
	}
	if (!excludedchannels) {
		acc.push(component);
		return acc;
	}

	const excludedChannels = new Set(excludedchannels);
	if (excludedChannels.has('app')) {
		return acc;
	}
	acc.push(component);
	return acc;
}

// Derived from schema.graphql's `Issue` type (topic 5's "Issue/Article/Style
// derive from generated types" item). Optionality below matches the
// schema's nullability, not what earlier hand-rolled assumptions claimed --
// `name`, `currency`, `price`, and `features` are the schema's only
// genuinely required Issue fields; everything else the server can send back
// null for. Uses plain `field?: T` rather than `field?: T | null`, matching
// this codebase's existing convention everywhere else (every consumer
// already treats a missing value with `!field`/`field ? ... : ...`, which
// doesn't distinguish null from undefined) -- `| null` would technically be
// more literal to the wire format but conflicts with hundreds of existing
// prop types elsewhere in the app that only accept `T | undefined`.
// `articles`/`featuredArticles`/`styles` are non-null LISTS of nullable
// ITEMS in the schema ([Article]!, [Style!]! for styles specifically is
// non-null items) -- articles/featuredArticles keep null items filtered out
// as a deliberate fetch-boundary contract (see shared/lib/use-issue.ts), so
// callers can keep treating `issue.articles` as clean `Array<Article>`
// instead of re-deriving that filter everywhere.
export interface Issue {
	id: string;
	IssueID?: string;
	name: string;
	description?: string;
	textDirection?: string;
	index?: number;
	cover?: string;
	created?: string;
	updated?: string;
	releaseDate?: string;
	features: Array<string>;
	isFree?: boolean;
	isPaid?: boolean;
	isPurchased?: boolean;
	position?: number;
	price: number;
	currency: string;
	articles?: Array<Article>;
	styles?: Array<Style>;
	featuredArticles?: Array<Article>;
	pages?: unknown;
	data?: unknown;
	slug?: string;
	externalUrl?: string;
}

export interface Section {
	id: string;
	name: string;
	position: number;
	issues?: Array<Issue>;
}

// Derived from schema.graphql's `Article` type. `id` and `features` are the
// only fields the schema requires; `name`/`channel`/`components`/
// `styleProperties` are JSON scalars (no schema shape) -- `name` is
// documented as the multi-language map every consumer already treats it as
// (`article.name['en']` throughout the app, same pattern as Article/Issue
// caption/credit fields elsewhere on this branch), the rest stay `unknown`
// since nothing in this pass reconciles the CMS component/style-properties
// schemas (that's the separate, harder VirtualStyleBuilder/VirtualCSS +
// components/article any-burn-down work). `replica`/`audios`/
// `styleProperties` are real schema fields this interface was missing
// entirely before.
export interface Article {
	id: string;
	ArticleID?: string;
	IssueID?: string;
	issueSlug?: string;
	role?: string;
	name?: Record<string, string>;
	author?: string;
	description?: string;
	thumbnail?: string;
	featureImage?: string;
	features: Array<string>;
	index?: number;
	isFeatured?: boolean;
	style?: string;
	styleProperties?: unknown;
	components?: unknown;
	created?: string;
	updated?: string;
	releaseDate?: string;
	keywords?: Array<string>;
	styles: Array<Style>;
	audio?: string;
	audios?: Array<ArticleAudio>;
	pages?: Array<string>;
	channel?: unknown;
	slug?: string;
	replica?: Array<ReplicaPage>;
	// Not a schema field -- `Responsive.hooks.ts`'s `useInternalLink` stamps
	// this onto an in-memory article object when an in-article link targets
	// a specific anchor, so `Issue.hooks.ts`'s `useArticle` can scroll to it
	// after navigation. Client-side-only runtime state, not server data.
	hash?: string;
}

export interface ArticleAudio {
	id: string;
	key: string;
	lang: string;
	url: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface ReplicaPage {
	name?: string;
	page?: number;
	image?: string;
	hotspots?: Array<ReplicaHotspot>;
}

export interface ReplicaHotspot {
	url?: string;
	area?: {
		x?: number;
		y?: number;
		width?: number;
		height?: number;
	};
}

export function fixInvalidInPages(page: string) {
	if (!page.replace) {
		return page;
	}
	return page
		.replace(
			/https:\/\/cdn\.pwa\.canvasflow\.io\/https:\/\/cdn\.pwa\.canvasflow\.io/,
			'https://cdn.pwa.canvasflow.io',
		)
		.replace(
			/https:\/\/cdn\.pwa\.cflowdev\.com\/https:\/\/cdn\.pwa\.cflowdev\.com/,
			'https://cdn.pwa.cflowdev.com',
		);
}

export function getImagesFromIssue(images: Set<string>, issues: Array<Issue>) {
	for (const issue of issues) {
		const { cover, featuredArticles } = issue;
		if (cover) {
			images.add(cover);
		}
		if (!featuredArticles) {
			continue;
		}

		for (const article of featuredArticles) {
			const { thumbnail, featureImage } = article;
			if (thumbnail) {
				images.add(thumbnail);
			}

			if (featureImage) {
				images.add(featureImage);
			}
		}
	}
}

// Derived from schema.graphql's `Style` type. Only `id` is required; every
// other field is nullable, including `properties` and `supportedDevices`
// (both `JSON` scalars with no schema shape -- `supportedDevices` arrives
// over the wire as whatever the CMS wrote, an array in practice, and is
// only turned into a `Set<DeviceType>` by
// components/article/virtual/VirtualStyleBuilder.ts's own processing step,
// not at the fetch boundary). Both stay `unknown` here rather than lying
// about a shape this pass doesn't reconcile -- that reconciliation is the
// CSS-generation engine work already flagged separately in
// improvements/DASHBOARD.md (VirtualStyleBuilder.ts/VirtualCSS.ts's own
// any burn-down), which is the only place besides the equivalent
// non-virtual components/article/ tree that reads these two fields as
// actual values rather than passing them through a GraphQL selection.
export interface Style {
	id: string;
	name?: string;
	description?: string;
	properties?: unknown;
	supportedDevices?: unknown;
	type?: string;
	// `string` on the wire; `StyleBuilder.mergeParentProperties` sets this to
	// `null` once inheritance is resolved, so a second pass over the same
	// style array doesn't re-merge it (see StyleBuilder.test.ts's "clears
	// parent to null" expectation).
	parent?: string | null;
	tablet?: string;
	desktop?: string;
	created?: string;
	lastModified?: string;
}

export interface Font {
	name: string;
	urls: Array<string>;
}

export type DeviceType = 'mobile' | 'desktop' | 'tablet';

// Non-standard PWA install-prompt event, not part of lib.dom.d.ts.
// Shared by App.tsx/App.context.tsx (capture + store), RootRoute.tsx
// (window global declaration), and every template's install-prompt UI
// (deferredPrompt.prompt()).
export interface BeforeInstallPromptEvent extends Event {
	readonly platforms: Array<string>;
	readonly userChoice: Promise<{
		outcome: 'accepted' | 'dismissed';
		platform: string;
	}>;
	prompt(): Promise<void>;
}

export function getAppToken(): undefined | string | null {
	if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
		return undefined;
	}

	return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function clone<T>(obj: T): T {
	if (structuredClone) {
		return structuredClone(obj);
	}

	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

export function getAppUser(): undefined | string | null {
	const userId = localStorage.getItem(APP_USER_ID);
	if (!userId) {
		return undefined;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		return userId;
	}

	return undefined;
}

export function getAccessToken(): undefined | string | null {
	let accessToken = undefined;
	if (getUserRole() === 'admin') {
		return getAppToken();
	}

	accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
	if (!accessToken) {
		return undefined;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		return accessToken;
	}

	return undefined;
}

export function getUserRole(): undefined | string {
	const role = localStorage.getItem(USER_ROLE);
	if (!role) {
		return undefined;
	}

	return role;
}

export function isNumeric(num: string | number) {
	return !isNaN(Number(num));
}

export type NavbarVisibility = 'visible' | 'hidden';

const RTL_LANGUAGES = new Set(['ar', 'he']);

export function isRtlLanguage(language?: string): boolean {
	if(language === undefined) return false
	return RTL_LANGUAGES.has(language);
}

// TOOD This functions is only for hacking features
export function mapIssuesNonImplementedFeatures(issue: Issue): Issue {
	issue = clone(issue);
	if (!issue.features) {
		issue.features = [];
	}

	if (issue.articles) {
		issue.articles = issue.articles.map(mapArticlesNonImplementedFeatures);
	}

	if (issue.featuredArticles) {
		issue.featuredArticles = issue.featuredArticles.map(
			mapArticlesNonImplementedFeatures,
		);
	}

	return issue;
}

// TOOD This functions is only for hacking features
export function mapArticlesNonImplementedFeatures(article: Article): Article {
	article = clone(article);
	if (!article.features) {
		article.features = [];
	}

	return article;
}
